import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
  Plugin,
  ScriptableContext,
  ScriptableLineSegmentContext,
  ChartEvent,
  ActiveElement,
  Chart,
  ChartData,
  ChartTypeRegistry,
} from "chart.js";
import TickerPriceTag from "../components/TickerPriceTag";
import "chartjs-adapter-date-fns";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  CandlestickController,
  CandlestickElement,
} from "chartjs-chart-financial";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import { cloneDeep } from "lodash";
import { useAppContext } from "../components/AppContext";
import {
  accentButtonBackground,
  accentButtonText,
  accentButtonTextWeight,
  buttonGrey,
  darkModeScale,
  darkModeText,
  darkModeWidgetBackground,
  lightModeScale,
  lightModeText,
  lightModeWidgetBackground,
  negativeRed,
  negativeRedVolume,
  positiveGreen,
  positiveGreenVolume,
  primaryBlue,
} from "../util/style";
import { ChartParams, ChartDataResult, IntervalOption } from "../types";

// Augment Chart.js plugin options to include custom plugins
declare module 'chart.js' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends keyof ChartTypeRegistry> {
    crosshairPlugin?: {
      timestamps?: number[];
      chartParams?: ChartParams;
      showCandleChart?: boolean;
      darkMode?: boolean;
      fontSize?: string;
    };
    horizontalLineLabel?: Record<string, unknown>;
  }
}

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  TimeScale,
  Tooltip,
  Legend,
  CandlestickController,
  CandlestickElement,
);

interface SignalPoint {
  timestamp: number;
  x: number;
  y: number;
  action: 'buy' | 'sell';
  share: number;
}

interface CandleData {
  o: number | null;
  c: number | null;
  h?: number | null;
  l?: number | null;
  x: Date;
}

interface VolumeData {
  y: number | null;
  x: Date;
}

interface TradePointDetail {
  detail?: {
    type: string;
    date: number;
    timestamp: number;
    price: string;
    share: number;
  };
  clickPosition?: {
    x: number;
    y: number;
  };
}

const formatTimestamp = (timestamp: number, chartParams: ChartParams, crosshair: boolean = false): string[] => {
  const date = new Date(timestamp);

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const month = monthNames[date.getMonth()];
  const weekday = weekdayNames[date.getDay()];
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  let array: string[] = [];
  if (["1d"].includes(chartParams?.range)) {
    array = [`${hours}:${minutes}`];
  } else if (["5d"].includes(chartParams?.range)) {
    array = crosshair
      ? [`${hours}:${minutes}`]
      : [`${weekday}`, `${month} ${day}`];
  } else if (["1mo"].includes(chartParams?.range)) {
    array = crosshair ? [`${month} ${day}`] : [`${month} ${day}`, `${year}`];
  } else if (["3mo", "6mo", "ytd", "1y"].includes(chartParams?.range)) {
    array = crosshair ? [`${month} ${day}`] : [`${month}`, `${year}`];
  } else if (["2y"].includes(chartParams?.range)) {
    array = crosshair ? [`${month} ${day}`] : [`${month}`, `${year}`];
  } else if (["5y", "10y", "max"].includes(chartParams?.range)) {
    array = crosshair ? [`${month} ${day}`] : [`${year}`];
  } else {
    array = [`${month} ${day}`, `${year}`];
  }

  return array;
};

const horizontalLineLabelPlugin: Plugin<'line'> = {
  id: "horizontalLineLabel",
  afterDatasetsDraw(chart: Chart<'line'>) {
    const {
      ctx,
      chartArea: { left },
      scales: { y },
    } = chart;

    chart.data.datasets.forEach((dataset) => {
      if (
        dataset.data.length &&
        dataset.data.every((v) => v === dataset.data[0]) &&
        (dataset.label?.toLowerCase().includes("cumulative") ||
          dataset.label?.toLowerCase().includes("unrealized") ||
          dataset.label?.toLowerCase().includes("current"))
      ) {
        const meta = (dataset as { meta?: { color?: string; originalValue?: number } }).meta;
        const color = meta?.color ?? (dataset.borderColor as string) ?? "black";
        const originalValue = meta?.originalValue ?? (dataset.data[0] as number);
        const fontSize = 12;
        const yValue = y.getPixelForValue(dataset.data[0] as number);

        const text = `${dataset.label}: $${Number(originalValue)?.toFixed(2)}`;
        const padding = 4;
        const textHeight = 14;
        const xOffset = 4;

        ctx.save();
        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";

        const textWidth = ctx.measureText(text).width;
        const tagY = yValue;

        ctx.fillStyle = color;
        ctx.fillRect(
          left + xOffset - padding,
          tagY - textHeight / 2,
          textWidth + padding * 2,
          textHeight,
        );

        ctx.fillStyle = "white";
        ctx.fillText(text, left + xOffset, tagY);

        ctx.restore();
      }
    });
  },
};

const crosshairPlugin: Plugin<'line'> = {
  id: "crosshairPlugin",
  afterEvent(chart: Chart<'line'>, args: { event: ChartEvent }) {
    (chart as Chart<'line'> & { _mousePosition?: ChartEvent })._mousePosition = args.event;
  },
  afterDraw(chart: Chart<'line'>) {
    const mouse = (chart as Chart & { _mousePosition?: ChartEvent })._mousePosition;

    if (!mouse) return;

    const ctx = chart.ctx;
    const x = mouse.x || 0;
    const y = mouse.y || 0;

    const { top, bottom, left, right } = chart.chartArea;
    if (x < left || x > right || y < top || y > bottom) return;

    const yValue = chart.scales.y.getValueForPixel(y);
    const xValue = chart.scales.x.getValueForPixel(x);

    const pluginOptions = chart.options.plugins?.crosshairPlugin as {
      timestamps?: number[];
      chartParams?: ChartParams;
      fontSize?: string;
    } | undefined;

    const timestamps = pluginOptions?.timestamps || [];
    const chartParams = pluginOptions?.chartParams;
    const fontSize = pluginOptions?.fontSize || "12px";

    let nearestIndex = 0;
    let minDiff = Infinity;

    timestamps.forEach((t, i) => {
      const diff = Math.abs((xValue || 0) - t * 1000);
      if (diff < minDiff) {
        minDiff = diff;
        nearestIndex = i;
      }
    });

    const rawTimestamp = timestamps[nearestIndex];
    const formattedPrice = `$${Number(yValue).toFixed(2)}`;
    const formattedDate = chartParams
      ? formatTimestamp(rawTimestamp * 1000, chartParams, true).join(", ")
      : "";

    ctx.save();
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = "rgb(125,125,125)";

    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(right, y);
    ctx.stroke();

    ctx.beginPath();
    ctx.setLineDash([]);
    ctx.fillStyle = "rgb(125,125,125)";
    ctx.arc(x, y, 3, 0, 2 * Math.PI);
    ctx.fill();

    ctx.font = `bold ${fontSize} sans-serif`;

    const priceX = left + 4;
    const priceY = y;
    const pricePadding = 4;
    const priceTextWidth = ctx.measureText(formattedPrice).width;
    const priceTextHeight = 14;

    ctx.fillStyle = "rgb(100,100,100)";
    ctx.fillRect(
      priceX - pricePadding,
      priceY - priceTextHeight / 2,
      priceTextWidth + pricePadding * 2,
      priceTextHeight,
    );

    ctx.fillStyle = "white";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(formattedPrice, priceX, priceY);

    const datePadding = 4;
    const dateTextWidth = ctx.measureText(formattedDate).width;
    const dateTextHeight = 14;

    const dateRectY = bottom - dateTextHeight;
    const dateTextY = dateRectY + dateTextHeight / 2;

    ctx.fillStyle = "rgb(100,100,100)";
    ctx.fillRect(
      x - dateTextWidth - datePadding * 2,
      dateRectY,
      dateTextWidth + datePadding * 2,
      dateTextHeight,
    );

    ctx.fillStyle = "white";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText(formattedDate, x - datePadding, dateTextY);

    ctx.restore();
  },
};

ChartJS.register(crosshairPlugin);

interface ChartsProps {
  handleWatchListAddRemove: (ticker: string) => void;
}

const Charts: React.FC<ChartsProps> = ({ handleWatchListAddRemove }) => {
  const {
    user,
    isLogged,
    chartParams,
    getFont,
    showCandleChart,
    setShowCandleChart,
    showVolume,
    setShowVolume,
    showWick,
    setShowWick,
    showTrades,
    setShowTrades,
    rangeOption2,
    windowSecondsByInterval,
    intervalOption,
    darkMode,
    isDraggingChart,
    setIsDraggingChart,
    selectedTicker,
    selectedAccount,
    expandChart,
    setExpandChart,
    setExpandWatchlist,
    selectedTradePointDetail,
    setSelectedTradePointDetail,
    zoomPortionIndex,
    setZoomPortionIndex,
    lastPrice,
    prevClosePrice,
    lastClosePriceDelta,
    lastClosePriceCompare,
    tradesDataArray,
    chartDataSelected,
    zoomFilteredChartData,
    selectedTickerCurrency,
    findFilteredIndex,
    privateMode,
    adminMode,
    numFormat,
    handleChangeParam,
    getHoldingInfo,
    getSplitMultiplier,
    testPrices,
    setTestPrices,
  } = useAppContext() || {};

  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const scrollBlockRef = useRef<HTMLDivElement | null>(null);

  const typedZoomFilteredChartData = zoomFilteredChartData as ChartDataResult | undefined;
  const typedChartDataSelected = chartDataSelected as ChartDataResult | undefined;

  const signalPoints = useMemo((): SignalPoint[] => {
    const splits = (typedZoomFilteredChartData as ChartDataResult & { events?: { splits?: Record<string, { date: number; numerator: number; denominator: number }> } })?.events?.splits;
    const splitAdjustments = Object.values(splits || {}).sort(
      (a, b) => a.date - b.date,
    );

    const getSplitAdjustedPrice = (price: number, timestamp: number): number => {
      let adjustedPrice = price;
      splitAdjustments.forEach((split) => {
        if (timestamp < split.date) {
          adjustedPrice *= split.denominator / split.numerator;
        }
      });
      return adjustedPrice;
    };

    const windowSeconds = windowSecondsByInterval?.[chartParams?.interval || '1d'] || 0;

    return (typedZoomFilteredChartData?.timestamp
      ?.map((timestamp: number, index: number) => {
        let match: { unix: number; share: number; price: number | string } | false = false;
        let tradePrice = 0;

        tradesDataArray?.forEach((s) => {
          if (Math.abs(s.unix - timestamp) <= windowSeconds) {
            match = s;
            tradePrice = Number(s.price);
          }
        });

        if (
          match &&
          (findFilteredIndex?.minIndex || 0) <= index &&
          (findFilteredIndex?.maxIndex || 0) >= index
        ) {
          const adjustedY = getSplitAdjustedPrice(tradePrice, timestamp);

          return {
            timestamp,
            x: timestamp * 1000,
            y: adjustedY,
            action: (match as { share: number }).share > 0 ? "buy" : "sell",
            share: (match as { share: number }).share,
          } as SignalPoint;
        }
        return null;
      })
      .filter((pt): pt is SignalPoint => pt !== null)) || [];
  }, [
    typedZoomFilteredChartData,
    findFilteredIndex,
    tradesDataArray,
    chartParams?.interval,
    windowSecondsByInterval,
  ]);

  const buyPoints = signalPoints?.filter((pt) => pt.action === "buy");
  const sellPoints = signalPoints?.filter((pt) => pt.action === "sell");

  const datasetArray = useMemo((): (number | null)[] | undefined => {
    return typedZoomFilteredChartData?.indicators?.quote?.[0]?.close?.filter(
      (_, idx) => {
        return (
          idx >= (findFilteredIndex?.minIndex || 0) && idx <= (findFilteredIndex?.maxIndex || 0)
        );
      },
    );
  }, [typedZoomFilteredChartData, findFilteredIndex]);

  const candleArray = useMemo((): CandleData[] => {
    const tempArray: CandleData[] = [];
    typedZoomFilteredChartData?.indicators?.quote?.[0]?.close?.forEach((_, idx) => {
      if (
        idx >= (findFilteredIndex?.minIndex || 0) &&
        idx <= (findFilteredIndex?.maxIndex || 0)
      ) {
        const target = typedZoomFilteredChartData?.indicators?.quote?.[0];
        const obj: CandleData = {
          o: target?.open?.[idx] ?? null,
          c: target?.close?.[idx] ?? null,
          x: new Date((typedZoomFilteredChartData?.timestamp?.[idx] || 0) * 1000),
        };
        if (showWick) {
          obj.h = target?.high?.[idx] ?? null;
          obj.l = target?.low?.[idx] ?? null;
        }
        tempArray.push(obj);
      }
    });
    return tempArray;
  }, [typedZoomFilteredChartData, findFilteredIndex, showWick]);

  const volumeDataset = useMemo((): VolumeData[] => {
    const tempArray: VolumeData[] = [];
    typedZoomFilteredChartData?.indicators?.quote?.[0]?.close?.forEach((_, idx) => {
      if (
        idx >= (findFilteredIndex?.minIndex || 0) &&
        idx <= (findFilteredIndex?.maxIndex || 0)
      ) {
        const target = typedZoomFilteredChartData?.indicators?.quote?.[0];
        tempArray.push({
          y: target?.volume?.[idx] ?? null,
          x: new Date((typedZoomFilteredChartData?.timestamp?.[idx] || 0) * 1000),
        });
      }
    });
    return tempArray;
  }, [typedZoomFilteredChartData, findFilteredIndex]);

  const datasetArrayBoundary = useMemo(() => {
    let min = Number.MAX_SAFE_INTEGER;
    let max = 0;

    datasetArray?.forEach((eachNumber) => {
      if (eachNumber && eachNumber > max) {
        max = eachNumber;
      }
      if (eachNumber && eachNumber < min) {
        min = eachNumber;
      }
    });
    if (datasetArray) {
      return { min, max, offset: (max - min) * 0.05 };
    } else {
      return { min: 0, max: 0, offset: 1 };
    }
  }, [datasetArray]);

  const costAverage = useMemo(() => {
    return getHoldingInfo?.(selectedAccount || '', selectedTicker || '') || { costBasis: null, costBasisWithRealized: null };
  }, [getHoldingInfo, selectedAccount, selectedTicker]);

  const currentPriceLineStyle = useMemo(() => {
    const dataLength = datasetArray?.length || 0;
    const pointRadiusArray = Array(dataLength).fill(0);
    const pointStyleArray = Array(dataLength).fill("circle");

    if (dataLength > 0) {
      pointRadiusArray[dataLength - 1] = 5;
      pointStyleArray[dataLength - 1] = "rect";
    }
    return { pointStyleArray, pointRadiusArray };
  }, [datasetArray]);

  function getMarketStatus(unixSeconds: number): string {
    const date = new Date(unixSeconds * 1000);

    const nyTime = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const hour = parseInt(nyTime.find((p) => p.type === "hour")?.value || "0", 10);
    const minute = parseInt(nyTime.find((p) => p.type === "minute")?.value || "0", 10);
    const totalMinutes = hour * 60 + minute;

    if (totalMinutes >= 4 * 60 && totalMinutes < 9 * 60 + 30) {
      return "PRE-MARKET";
    } else if (totalMinutes >= 9 * 60 + 30 && totalMinutes < 16 * 60) {
      return "MARKET-HOUR";
    } else if (totalMinutes >= 16 * 60 && totalMinutes < 20 * 60) {
      return "AFTER-MARKET";
    } else {
      return "CLOSED";
    }
  }

  const getTimeColor = useCallback(
    (ctx: ScriptableLineSegmentContext) => {
      const i = ctx.p0DataIndex;
      const time = typedZoomFilteredChartData?.timestamp?.[i] || 0;
      const status = getMarketStatus(time);
      if (status === "PRE-MARKET") return "orange";
      if (status === "MARKET-HOUR") return primaryBlue;
      if (status === "AFTER-MARKET") return "grey";
      if (!["MAX"].includes(chartParams?.range || '')) return primaryBlue;
      if (status === "CLOSED") return "transparent";
      return "black";
    },
    [typedZoomFilteredChartData, chartParams],
  );

  const data = useMemo(() => {
    const tempObj: {
      labels: number[];
      datasets: Record<string, unknown>[];
    } = {
      labels: typedZoomFilteredChartData?.timestamp
        ?.slice(findFilteredIndex?.minIndex || 0, (findFilteredIndex?.maxIndex || 0) + 1)
        .map((t: number) => t * 1000) || [],
      datasets: [
        {
          label: "Price",
          data: !showCandleChart ? datasetArray : candleArray,
          type: !showCandleChart ? "" : "candlestick",
          segment: {
            borderColor: (ctx: ScriptableLineSegmentContext) => getTimeColor(ctx),
          },
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 1,
          spanGaps: false,
        },
        {
          label: "Volume",
          data: volumeDataset,
          type: "bar",
          yAxisID: "volume",
          hidden: !showVolume,
          backgroundColor: candleArray.map((candle) =>
            (candle.c || 0) > (candle.o || 0) ? positiveGreenVolume : negativeRedVolume,
          ),
          barPercentage: 0.5,
          categoryPercentage: 1.0,
        },
        {
          label: "Current",
          data: datasetArray?.map(() => lastPrice?.[selectedTicker || '']),
          borderColor:
            lastClosePriceCompare === "positive"
              ? datasetArray?.map((_, idx) =>
                  datasetArray.length - 1 === idx ? primaryBlue : positiveGreen,
                )
              : lastClosePriceCompare === "negative"
                ? datasetArray?.map((_, idx) =>
                    datasetArray.length - 1 === idx ? primaryBlue : negativeRed,
                  )
                : datasetArray?.map((_, idx) =>
                    datasetArray.length - 1 === idx ? primaryBlue : primaryBlue,
                  ),
          backgroundColor:
            lastClosePriceCompare === "positive"
              ? datasetArray?.map((_, idx) =>
                  datasetArray.length - 1 === idx ? primaryBlue : positiveGreen,
                )
              : lastClosePriceCompare === "negative"
                ? datasetArray?.map((_, idx) =>
                    datasetArray.length - 1 === idx ? primaryBlue : negativeRed,
                  )
                : datasetArray?.map((_, idx) =>
                    datasetArray.length - 1 === idx ? primaryBlue : primaryBlue,
                  ),
          tension: 0,
          pointHoverRadius: 0,
          pointRadius: currentPriceLineStyle.pointRadiusArray,
          pointStyle: currentPriceLineStyle.pointStyleArray,
          borderWidth: 1,
          borderDash: [10, 5],
          meta: {
            currency: selectedTickerCurrency,
            color:
              lastClosePriceCompare === "positive"
                ? positiveGreen
                : lastClosePriceCompare === "negative"
                  ? negativeRed
                  : primaryBlue,
          },
        },
      ],
    };

    if (showTrades) {
      tempObj.datasets = [
        ...tempObj.datasets,
        {
          label: "Buy",
          data: buyPoints,
          backgroundColor: positiveGreen,
          borderColor: positiveGreen,
          pointRadius: 4,
          pointStyle: "circle",
          showLine: false,
          pointHoverRadius: 8,
        },
        {
          label: "Sell",
          data: sellPoints,
          backgroundColor: negativeRed,
          borderColor: negativeRed,
          pointRadius: 4,
          pointStyle: "circle",
          showLine: false,
          pointHoverRadius: 8,
        },
      ];
    }
    if (
      showTrades &&
      costAverage.costBasis &&
      costAverage.costBasis > 0
    ) {
      let adjustedLine = costAverage.costBasis;
      if (datasetArrayBoundary.min > costAverage.costBasis) {
        adjustedLine = datasetArrayBoundary.min - datasetArrayBoundary.offset;
      }
      if (datasetArrayBoundary.max < costAverage.costBasis) {
        adjustedLine = datasetArrayBoundary.max + datasetArrayBoundary.offset;
      }

      tempObj.datasets.push({
        label: "Unrealized",
        data: datasetArray?.map(() => adjustedLine),
        borderColor: "orange",
        backgroundColor: "orange",
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 1,
        meta: {
          originalValue: costAverage.costBasis,
          currency: selectedTickerCurrency,
          color: "orange",
          fontSize: getFont?.(12),
        },
      });
    }
    if (
      showTrades &&
      costAverage.costBasisWithRealized &&
      costAverage.costBasisWithRealized > 0
    ) {
      let adjustedLine = costAverage.costBasisWithRealized;
      if (datasetArrayBoundary.min > costAverage.costBasisWithRealized) {
        adjustedLine = datasetArrayBoundary.min - datasetArrayBoundary.offset;
      }
      if (datasetArrayBoundary.max < costAverage.costBasisWithRealized) {
        adjustedLine = datasetArrayBoundary.max + datasetArrayBoundary.offset;
      }

      tempObj.datasets.push({
        label: "Cumulative",
        data: datasetArray?.map(() => adjustedLine),
        borderColor: primaryBlue,
        backgroundColor: primaryBlue,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 1,
        meta: {
          originalValue: costAverage.costBasisWithRealized,
          currency: selectedTickerCurrency,
          color: primaryBlue,
          fontSize: getFont?.(12),
        },
      });
    }
    return tempObj;
  }, [
    costAverage,
    buyPoints,
    sellPoints,
    datasetArray,
    lastClosePriceCompare,
    lastPrice,
    selectedTicker,
    datasetArrayBoundary,
    currentPriceLineStyle,
    selectedTickerCurrency,
    getTimeColor,
    typedZoomFilteredChartData,
    findFilteredIndex,
    candleArray,
    showCandleChart,
    volumeDataset,
    showVolume,
    showTrades,
    getFont,
  ]);

  const getTimeUnit = (range: string): 'hour' | 'day' | 'month' | 'year' | 'quarter' => {
    if (range === "1d") return "hour";
    if (["5d", "1mo"].includes(range)) return "day";
    if (["3mo", "6mo", "1y", "2y", "ytd"].includes(range)) return "month";
    if (["5y", "10y", "max"].includes(range)) return "year";
    return "quarter";
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      intersect: false,
    },
    onClick: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      if (!elements.length) return;

      const datasetIndex = elements[0].datasetIndex;
      const index = elements[0].index;

      const dataset = chart.data.datasets[datasetIndex];
      const point = dataset.data[index] as SignalPoint | undefined;

      if ((dataset.label === "Buy" || dataset.label === "Sell") && point) {
        const rect = chart.canvas.getBoundingClientRect();
        const detail = {
          type: dataset.label,
          date: point.x,
          timestamp: point.timestamp,
          price: numFormat?.(Number(point.y), false, 2) || "0.00",
          share: point.share * (getSplitMultiplier?.(selectedTicker || '', point.timestamp) || 1),
        };

        setSelectedTradePointDetail?.({
          detail,
          clickPosition: {
            x: (event.native as MouseEvent).clientX - rect.left,
            y: (event.native as MouseEvent).clientY - rect.top,
          },
        });
      }
    },
    onHover: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      const target = (event.native as MouseEvent)?.target as HTMLElement;
      if (elements.length) {
        const datasetLabel = chart.data.datasets[elements[0].datasetIndex]?.label;
        if (datasetLabel === "Buy" || datasetLabel === "Sell") {
          target.style.cursor = "pointer";
          return;
        }
      }
      target.style.cursor = "default";
    },
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
        text: selectedTicker,
      },
      tooltip: {
        enabled: false,
        mode: "index",
        intersect: false,
      },
      crosshairPlugin: {
        timestamps: typedZoomFilteredChartData?.timestamp ?? [],
        chartParams,
        showCandleChart,
        darkMode,
        fontSize: getFont?.(12),
      } as Record<string, unknown>,
      horizontalLineLabel: {},
    },
    scales: {
      x: {
        type: "time",
        offset: false,
        time: {
          unit: getTimeUnit(chartParams?.range || '1d'),
          tooltipFormat: "MMM d, yyyy HH:mm",
        },
        ticks: {
          maxTicksLimit: 10,
          callback: function (val: string | number) {
            return chartParams ? formatTimestamp(Number(val), chartParams) : '';
          },
          font: {
            size: Number(getFont?.(12)?.replace("px", "") || 12),
          },
          color: darkMode ? darkModeText : lightModeText,
          minRotation: 0,
          maxRotation: 0,
        },
        grid: {
          drawOnChartArea: true,
          drawTicks: true,
          color: "transparent",
        },
      },
      y: {
        min:
          datasetArrayBoundary.min < (lastPrice?.[selectedTicker || ''] || 0)
            ? datasetArrayBoundary.min - datasetArrayBoundary.offset > 0
              ? datasetArrayBoundary.min - datasetArrayBoundary.offset
              : 0
            : lastPrice?.[selectedTicker || ''],
        max:
          datasetArrayBoundary.max > (lastPrice?.[selectedTicker || ''] || 0)
            ? datasetArrayBoundary.max + datasetArrayBoundary.offset
            : lastPrice?.[selectedTicker || ''],
        ticks: {
          color: darkMode ? darkModeText : lightModeText,
          font: {
            size: Number(getFont?.(12)?.replace("px", "") || 12),
          },
          callback: function (value: string | number) {
            return Number(value).toFixed(2);
          },
        },
        beginAtZero: true,
        grid: {
          drawOnChartArea: true,
          drawTicks: true,
          color: darkMode ? darkModeScale : lightModeScale,
        },
      },
      volume: {
        position: "right",
        display: showVolume ? true : false,
        max: Math.max(...volumeDataset.map((each) => each.y || 0)) * 1.5,
        ticks: {
          color: darkMode ? darkModeText : lightModeText,
          font: {
            size: Number(getFont?.(12)?.replace("px", "") || 12),
          },
        },
      },
    },
  };

  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (boxRef.current && (selectedTradePointDetail as TradePointDetail)?.clickPosition) {
      const { width } = boxRef.current.getBoundingClientRect();
      setSelectedTradePointDetail?.((prev: TradePointDetail) => ({
        ...prev,
        clickPosition: {
          x: (prev.clickPosition?.x || 0) - width,
          y: prev.clickPosition?.y || 0,
        },
      }));
    }
  }, [(selectedTradePointDetail as TradePointDetail)?.clickPosition, setSelectedTradePointDetail]);

  const isDragging = useRef<boolean>(false);
  const dragStartIndex = useRef<number | null>(null);

  useEffect(() => {
    const element = scrollBlockRef.current;
    const chart = chartRef.current;
    const timestamps = typedChartDataSelected?.timestamp;
    if (!element || !chart || !timestamps?.length) return;

    const getIndexFromMouseX = (clientX: number): number | null => {
      const canvas = chart.canvas;
      const rect = canvas.getBoundingClientRect();
      const mouseX = clientX - rect.left;
      const xScale = chart.scales["x"];
      if (!xScale) return null;
      const xValue = xScale.getValueForPixel(mouseX);
      const timeStampNormalized = Math.floor((xValue || 0) / 1000);

      const findClosestIndex = (arr: number[], target: number): number => {
        let low = 0;
        let high = arr.length - 1;
        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          if (arr[mid] < target) {
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        }
        return low < arr.length ? low : -1;
      };

      return findClosestIndex(timestamps, timeStampNormalized);
    };

    const handleWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;

      e.preventDefault();

      const xIndex = getIndexFromMouseX(e.clientX);
      if (xIndex === null || xIndex < 0) return;

      const zoomAmount = e.deltaY;
      const { min, max } = zoomPortionIndex || { min: 0, max: timestamps.length - 1 };
      const range = max - min;
      const totalLength = timestamps.length - 1;
      const targetPercentile = (xIndex - min) / range;

      const newCenterIdx = range * targetPercentile + min;
      const eachScrollChange = 0.1;

      let minIndex = min;
      let maxIndex = max;

      if (zoomAmount < 0) {
        minIndex = (newCenterIdx - min) * eachScrollChange + min;
        maxIndex = max - (max - newCenterIdx) * eachScrollChange;
      } else if (zoomAmount > 0) {
        const minCandidate = min - (newCenterIdx - min) * eachScrollChange;
        const maxCandidate = (max - newCenterIdx) * eachScrollChange + max;
        minIndex = Math.max(minCandidate, 0);
        maxIndex = Math.min(maxCandidate, totalLength);
      }

      setZoomPortionIndex?.({
        min: Math.round(minIndex),
        max: Math.round(maxIndex),
      });
    };

    const handleMouseDown = (e: MouseEvent) => {
      const index = getIndexFromMouseX(e.clientX);
      if (index == null) return;
      isDragging.current = true;
      dragStartIndex.current = index;
      setIsDraggingChart?.(true);
    };

    const handleMouseMove = () => {
      return;
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDragging.current || dragStartIndex.current == null) return;

      isDragging.current = false;
      const dragEndIndex = getIndexFromMouseX(e.clientX);
      if (dragEndIndex == null) return;

      const indexDelta = dragStartIndex.current - dragEndIndex;
      const totalLength = timestamps.length - 1;
      const { min, max } = zoomPortionIndex || { min: 0, max: totalLength };

      let newMin = min + indexDelta;
      let newMax = max + indexDelta;

      if (newMin < 0) {
        newMax += -newMin;
        newMin = 0;
      }
      if (newMax > totalLength) {
        newMin -= newMax - totalLength;
        newMax = totalLength;
      }
      if (newMin >= 0 && newMax <= totalLength && newMax > newMin) {
        setZoomPortionIndex?.({
          min: Math.round(newMin),
          max: Math.round(newMax),
        });
      }

      dragStartIndex.current = null;
      setIsDraggingChart?.(false);
    };

    element.addEventListener("wheel", handleWheel, { passive: false });
    element.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      element.removeEventListener("wheel", handleWheel);
      element.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    zoomPortionIndex,
    setZoomPortionIndex,
    typedChartDataSelected,
    setIsDraggingChart,
  ]);

  const typedSelectedTradePointDetail = selectedTradePointDetail as TradePointDetail | undefined;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "36px",
        }}
      >
        <div
          onClick={() => {
            if (!expandChart) {
              setExpandWatchlist?.(false);
            }
            setExpandChart?.((prev: boolean) => !prev);
          }}
          style={{
            cursor: "pointer",
            borderTopLeftRadius: "3px",
            borderTopRightRadius: "3px",
            borderBottomLeftRadius: "3px",
            borderBottomRightRadius: "3px",
            marginRight: "5px",
            display: "flex",
            alignItems: "center",
            position: "absolute",
            top: 0,
            left: 0,
            fontWeight: accentButtonTextWeight,
            color: accentButtonText,
            background: accentButtonBackground,
          }}
        >
          {expandChart ? (
            <KeyboardArrowRightIcon style={{ fontSize: getFont?.(16) }} />
          ) : (
            <KeyboardArrowLeftIcon style={{ fontSize: getFont?.(16) }} />
          )}
        </div>
        <div
          style={{
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            marginRight: "auto",
            marginLeft: "10px",
          }}
        >
          {!typedZoomFilteredChartData?.meta?.longName && (
            <div>
              <div style={{ display: "flex", marginBottom: "2px", width: "100%" }}>
                {[150].map((eachWidth, idx) => (
                  <div
                    key={idx}
                    style={{
                      height: "14px",
                      width: eachWidth + "px",
                      background: darkMode ? "rgb(100,100,100)" : "rgb(230,230,230)",
                      borderRadius: "2px",
                      marginRight: "2px",
                    }}
                  />
                ))}
              </div>
              <div style={{ display: "flex" }}>
                {[100, 50, 25, 25].map((eachWidth, idx) => (
                  <div
                    key={idx}
                    style={{
                      height: "14px",
                      width: eachWidth + "px",
                      background: darkMode ? "rgb(100,100,100)" : "rgb(230,230,230)",
                      borderRadius: "2px",
                      marginRight: "2px",
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {typedZoomFilteredChartData?.meta?.longName && (
            <div
              style={{
                fontSize: getFont?.(16),
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div>
                {typedZoomFilteredChartData?.meta?.longName?.toUpperCase()}
              </div>
              {isLogged && (
                <div
                  onClick={() => {
                    handleWatchListAddRemove(selectedTicker || '');
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    marginLeft: "5px",
                  }}
                >
                  {user?.watchlist?.includes(selectedTicker || '') ? (
                    <StarIcon style={{ fontSize: getFont?.(14), color: "yellow" }} />
                  ) : (
                    <StarBorderIcon style={{ fontSize: getFont?.(14), color: "yellow" }} />
                  )}
                </div>
              )}
              {isLogged && (
                <input
                  type="text"
                  placeholder="$/Test"
                  value={
                    testPrices?.[selectedTicker || '']
                      ? `$${testPrices[selectedTicker || '']}`
                      : ""
                  }
                  style={{
                    marginLeft: 5,
                    outline: "none",
                    border: `1px solid ${
                      (testPrices?.[selectedTicker || ''] || 0) > 0 ? negativeRed : "grey"
                    }`,
                    color: darkMode ? darkModeText : lightModeText,
                    background: darkMode
                      ? darkModeWidgetBackground
                      : lightModeWidgetBackground,
                    fontSize: getFont?.(10),
                    height: 12,
                    width: 40,
                    borderRadius: 2,
                    cursor: "pointer",
                    padding: "2px 3px",
                  }}
                  onChange={(e) => {
                    const rawValue = e.target.value.trim();
                    const numericPart = rawValue.startsWith("$")
                      ? rawValue.slice(1)
                      : rawValue;

                    if (Number(numericPart) < 0) return;

                    setTestPrices?.((prev: Record<string, number>) => {
                      if (numericPart) {
                        return {
                          ...prev,
                          [selectedTicker || '']: Number(numericPart),
                        };
                      } else {
                        const updated = { ...prev };
                        delete updated[selectedTicker || ''];
                        return updated;
                      }
                    });
                  }}
                  onClick={() => {
                    setTestPrices?.((prev: Record<string, number>) => {
                      const updated = { ...prev };
                      delete updated[selectedTicker || ''];
                      return updated;
                    });
                  }}
                />
              )}
            </div>
          )}
          <div
            style={{
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
            }}
          >
            {typedZoomFilteredChartData?.meta?.longName && (
              <div style={{}}>
                <TickerPriceTag />
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            alignItems: "end",
            justifyContent: "space-evenly",
            height: "100%",
            fontSize: getFont?.(12),
          }}
        >
          <div
            style={{
              display: "flex",
              height: "fit-content",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: "600" }}>Range</div>&nbsp;
            <div style={{ display: "flex", marginTop: "1px" }}>
              {Object.keys(rangeOption2 || {})?.map((eachRangeOption, idx) => (
                <div
                  key={idx}
                  style={{
                    cursor: "pointer",
                    padding: "0px 3px",
                    fontWeight: accentButtonTextWeight,
                    background: ` ${
                      eachRangeOption === chartParams?.range
                        ? accentButtonBackground
                        : ""
                    }`,
                    color:
                      eachRangeOption === chartParams?.range
                        ? accentButtonText
                        : (rangeOption2?.[eachRangeOption] as IntervalOption[])?.includes(
                              chartParams?.interval as IntervalOption,
                            )
                          ? ""
                          : "grey",
                    borderRadius: "2px",
                  }}
                  onClick={() => {
                    handleChangeParam?.("range", eachRangeOption);
                  }}
                  className="hoverRangeIntervalButtons"
                >
                  {eachRangeOption.toUpperCase()}
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              height: "fit-content",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: "600" }}>Interval</div>&nbsp;
            <div style={{ display: "flex", marginTop: "2px" }}>
              {intervalOption?.map((eachIntervalOption: IntervalOption, idx: number) => (
                <div
                  key={idx}
                  style={{
                    cursor: "pointer",
                    padding: "0px 3px",
                    fontWeight: accentButtonTextWeight,
                    background: `${
                      eachIntervalOption === chartParams?.interval
                        ? accentButtonBackground
                        : ""
                    }`,
                    color:
                      eachIntervalOption === chartParams?.interval
                        ? accentButtonText
                        : (rangeOption2?.[chartParams?.range || ''] as IntervalOption[])?.includes(
                              eachIntervalOption,
                            )
                          ? ""
                          : "grey",
                    borderRadius: "2px",
                  }}
                  onClick={() => {
                    handleChangeParam?.("interval", eachIntervalOption);
                  }}
                  className="hoverRangeIntervalButtons"
                >
                  {eachIntervalOption.toUpperCase()}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            borderLeft: `1px solid ${buttonGrey}`,
            marginLeft: "5px",
            paddingLeft: "5px",
            height: "100%",
          }}
        >
          <div
            onClick={() => {
              setShowCandleChart?.((prev: boolean) => !prev);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              height: "50%",
              padding: "0 5px 0 0",
            }}
            className="hoverWatchlistBackground"
          >
            <input
              type="checkbox"
              checked={showCandleChart}
              readOnly
              style={{
                cursor: "pointer",
                accentColor: accentButtonBackground,
                margin: "0 2px 0 0",
                transform: "scale(0.75)",
              }}
            />
            <div style={{ fontWeight: "600", fontSize: getFont?.(10) }}>
              Candle
            </div>
          </div>
          <div
            onClick={() => {
              setShowVolume?.((prev: boolean) => !prev);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              height: "50%",
              padding: "0 5px 0 0",
            }}
            className="hoverWatchlistBackground"
          >
            <input
              type="checkbox"
              checked={showVolume}
              readOnly
              style={{
                cursor: "pointer",
                accentColor: accentButtonBackground,
                margin: "0 2px 0 0",
                transform: "scale(0.75)",
              }}
            />
            <div style={{ fontWeight: "600", fontSize: getFont?.(10) }}>
              Volume
            </div>
          </div>
        </div>
        <div
          style={{
            marginRight: "5px",
            paddingLeft: "5px",
            height: "100%",
          }}
        >
          <div
            onClick={() => {
              if (!showCandleChart) return;
              setShowWick?.((prev: boolean) => !prev);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: showCandleChart ? "pointer" : "",
              height: "50%",
              padding: "0 5px 0 0",
            }}
            className={showCandleChart ? "hoverWatchlistBackground" : ""}
          >
            <input
              type="checkbox"
              disabled={!showCandleChart}
              checked={showWick}
              readOnly
              style={{
                cursor: showCandleChart ? "pointer" : "",
                accentColor: accentButtonBackground,
                margin: "0 2px 0 0",
                transform: "scale(0.75)",
              }}
            />
            <div
              style={{
                fontWeight: "600",
                opacity: showCandleChart ? "" : "0.5",
                fontSize: getFont?.(10),
              }}
            >
              Wick
            </div>
          </div>
          <div
            onClick={() => {
              setShowTrades?.((prev: boolean) => !prev);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              height: "50%",
              padding: "0 5px 0 0",
            }}
            className="hoverWatchlistBackground"
          >
            <input
              type="checkbox"
              checked={showTrades}
              readOnly
              style={{
                cursor: "pointer",
                accentColor: accentButtonBackground,
                margin: "0 2px 0 0",
                transform: "scale(0.75)",
              }}
            />
            <div style={{ fontWeight: "600", fontSize: getFont?.(10) }}>
              Trades
            </div>
          </div>
        </div>
      </div>
      <div
        ref={scrollBlockRef}
        style={{
          flexGrow: 1,
          height: "90%",
          width: "100%",
          display: "flex",
          opacity: isDraggingChart ? "0.75" : "",
          overflow: "hidden",
        }}
      >
        <Line
          ref={chartRef}
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: "100%",
            width: "100%",
          }}
          data={data as unknown as ChartData<'line'>}
          options={options}
          plugins={[horizontalLineLabelPlugin]}
        />
      </div>
      {typedSelectedTradePointDetail?.detail && (
        <div
          style={{
            position: "absolute",
            top: typedSelectedTradePointDetail.clickPosition?.y,
            left: typedSelectedTradePointDetail.clickPosition?.x,
            transform: "translateX(-100%)",
            background: darkMode ? "rgb(30,30,30)" : "white",
            border: `1px solid ${
              darkMode ? "rgb(80,80,80)" : "rgb(200,200,200)"
            }`,
            padding: "4px 8px",
            borderRadius: "4px",
            boxShadow: "0 0 5px rgba(0,0,0,0.2)",
            fontSize: getFont?.(12),
          }}
        >
          <div>
            Date:&nbsp;
            <span>
              {new Date(
                typedSelectedTradePointDetail?.detail?.date,
              ).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
          <div>
            Traded:&nbsp;
            <span
              style={{
                color:
                  typedSelectedTradePointDetail?.detail?.type === "Sell"
                    ? negativeRed
                    : positiveGreen,
                fontWeight: "600",
              }}
            >
              {privateMode || adminMode
                ? typedSelectedTradePointDetail?.detail?.share
                : "********"}
              &nbsp;Shares
            </span>
          </div>
          <div>
            Price:&nbsp;
            <span style={{ fontWeight: "600" }}>
              ${typedSelectedTradePointDetail?.detail?.price}
            </span>
          </div>
          <div>
            Proceeds:&nbsp;$
            {numFormat?.(
              Number(typedSelectedTradePointDetail?.detail?.price) *
                Number(typedSelectedTradePointDetail?.detail?.share),
              true,
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Charts;
